import { useLocalSearchParams } from 'expo-router';
import { OtpSearchParams } from 'features/otp/constants/OtpSearchParams';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import useRedirect from 'features/otp/hooks/useRedirection/useRedirection';
import useResendOtp from 'features/otp/hooks/useResendOtp/useResendOtp';
import useVerifyChallenge from 'features/otp/hooks/useVerifyChallenge/useVerifyChallenge';
import OtpScreen from 'features/otp/OtpScreen';
import { EMAIL_VERIFICATION_TYPE } from 'features/signup/constants';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import { useAuth } from 'shared/providers/auth';

const VERIFY_EMAIL_SCREEN_ERROR_CODES = [3, 9, 11];

const VerifyEmailCode = () => {
  const { t: translatedTextOtp } = useTranslation('otp');

  const { verifyChallenge, isLoading: isVerifyChallengeLoading } =
    useVerifyChallenge();
  const {
    anonymousId,
    session,
    emailAddress,
    phoneNumber,
    screenType,
    flowKey,
    loginMethod,
    usernameType,
    deviceNotificationStatus,
  } = useLocalSearchParams<OtpSearchParams>();

  const { challengeRedirect } = useRedirect();
  const { initiateWhereToGo, isLoading: isWhereToGoFetching } = useWhereToGo();
  const { handleLogout } = useAuth();
  const resendOtp = useResendOtp();
  const [otpSubmitAttempt, setOtpSubmitAttempt] = useState(1);

  // Handle logout
  const handleLogoutWrapper = async () => {
    await handleLogout({
      shouldNavigateToLogin: true,
    });
  };

  useBackHandler(handleLogoutWrapper);

  const handleVerificationCodeSubmitted = usePressableAnalytics({
    analyticsId: 'verificationCodeSubmitted',
    customAttributes: {
      verification_attempt: otpSubmitAttempt,
      verification_type: 'email',
    },
  });

  const signedIn = usePressableAnalytics({
    analyticsId: 'signedIn',
    customAttributes: {
      login_method: loginMethod,
      username_type: usernameType,
      device_notification_status: deviceNotificationStatus,
    },
  });

  // Handler for OTP verification
  const handleVerifyOtp = async (code: string) => {
    handleVerificationCodeSubmitted();
    setOtpSubmitAttempt(otpSubmitAttempt + 1);
    const result = await verifyChallenge(
      {
        emailAddress: emailAddress ?? '',
        code: code,
        phoneNumber: phoneNumber ?? '',
        anonymousId: anonymousId ?? '',
        session: session ?? '',
      },
      {
        verifyType: EMAIL_VERIFICATION_TYPE,
      },
    );

    if (result.data?.challenge) {
      const { challenge, phoneLineNumber } = result.data || {};
      await challengeRedirect(
        challenge || '',
        emailAddress || '',
        phoneLineNumber || phoneNumber || '',
        phoneLineNumber || '',
        result.data?.session || '',
        screenType || '',
        flowKey,
      );
    }

    if (result.success) {
      signedIn();
      initiateWhereToGo();
    }

    return result;
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    return await resendOtp({
      anonymousId: anonymousId ?? '',
      emailAddress: emailAddress ?? '',
      phoneNumber: phoneNumber ?? '',
    });
  };

  // Custom description content for email verification
  const description = (
    <>
      {translatedTextOtp('emailVerification.description')}
      <BodyBaseText numberOfLines={0}>
        {`${emailAddress}. ${translatedTextOtp('emailVerification.subDescription')}.`}
      </BodyBaseText>
    </>
  );

  return (
    <OtpScreen
      // Flow information
      flowKey={flowKey}
      verificationType={OtpVerificationType.EMAIL}
      // Text content
      headerText={translatedTextOtp('emailVerification.header')}
      description={description}
      // External state
      isLoading={isVerifyChallengeLoading || isWhereToGoFetching}
      // Configurable error codes
      screenLevelErrorCodes={VERIFY_EMAIL_SCREEN_ERROR_CODES}
      // Event handlers
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={handleLogoutWrapper}
    />
  );
};

export default VerifyEmailCode;
